import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

type Category = '전체' | '언론' | '미디어' | '캠페인';

interface Resource {
  id: string;
  title: string;
  url: string | null;
  thumbnail: string | null;
  category: string;
  description: string | null;
  file_url: string | null;
  file_name: string | null;
  published_at: string | null;
  created_at: string;
}

interface ResourceFormData {
  title: string;
  url: string;
  thumbnail: string;
  category: string;
  description: string;
  file_url: string;
  file_name: string;
  published_at: string;
}

export default function ResourcePage() {
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category>('전체');
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<{ url: string; title: string } | null>(null);

  // Admin states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState<ResourceFormData>({
    title: '',
    url: '',
    thumbnail: '',
    category: '언론',
    description: '',
    file_url: '',
    file_name: '',
    published_at: new Date().toISOString().split('T')[0],
  });

  const categories: Category[] = ['전체', '언론', '미디어', '캠페인'];

  const categoryColors = {
    언론: 'bg-red-100 text-red-700',
    미디어: 'bg-blue-100 text-blue-700',
    캠페인: 'bg-teal-100 text-teal-700',
  };

  useEffect(() => {
    fetchResources();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResources(data || []);
    } catch (err) {
      console.error('자료실 데이터 로딩 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredResources =
    selectedCategory === '전체'
      ? resources
      : resources.filter((r) => r.category === selectedCategory);

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(fileUrl, '_blank');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Admin: Password verification
  const handlePasswordSubmit = () => {
    if (adminPassword === '100-justice') {
      setIsAuthenticated(true);
      setShowPasswordModal(false);
      setShowFormModal(true);
      setAdminPassword('');
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const handleAddResourceClick = () => {
    setEditingResource(null);
    setFormData({
      title: '',
      url: '',
      thumbnail: '',
      category: '언론',
      description: '',
      file_url: '',
      file_name: '',
      published_at: new Date().toISOString().split('T')[0],
    });
    setShowPasswordModal(true);
    setAdminPassword('');
    setPasswordError(false);
  };

  const handleEditResource = (resource: Resource) => {
    if (!isAuthenticated) {
      setToast({ type: 'error', text: '관리자 인증이 필요합니다.' });
      return;
    }
    setEditingResource(resource);
    setFormData({
      title: resource.title,
      url: resource.url || '',
      thumbnail: resource.thumbnail || '',
      category: resource.category,
      description: resource.description || '',
      file_url: resource.file_url || '',
      file_name: resource.file_name || '',
      published_at: resource.published_at?.split('T')[0] || new Date().toISOString().split('T')[0],
    });
    setShowFormModal(true);
  };

  const handleDeleteResource = async (id: string) => {
    if (!isAuthenticated) {
      setToast({ type: 'error', text: '관리자 인증이 필요합니다.' });
      return;
    }
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase.from('resources').delete().eq('id', id);
      if (error) throw error;
      setToast({ type: 'success', text: '자료가 삭제되었습니다.' });
      await fetchResources();
    } catch (err) {
      console.error('삭제 실패:', err);
      setToast({ type: 'error', text: '삭제에 실패했습니다.' });
    }
  };

  const handleSubmitResource = async () => {
    if (!formData.title.trim()) {
      setToast({ type: 'error', text: '제목을 입력해주세요.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const resourceData = {
        title: formData.title.trim(),
        url: formData.url.trim() || null,
        thumbnail: formData.thumbnail.trim() || null,
        category: formData.category,
        description: formData.description.trim() || null,
        file_url: formData.file_url.trim() || null,
        file_name: formData.file_name.trim() || null,
        published_at: formData.published_at || null,
      };

      if (editingResource) {
        const { error } = await supabase
          .from('resources')
          .update(resourceData)
          .eq('id', editingResource.id);
        if (error) throw error;
        setToast({ type: 'success', text: '자료가 수정되었습니다.' });
      } else {
        const { error } = await supabase.from('resources').insert([resourceData]);
        if (error) throw error;
        setToast({ type: 'success', text: '자료가 등록되었습니다.' });
      }

      setShowFormModal(false);
      setEditingResource(null);
      await fetchResources();
    } catch (err) {
      console.error('저장 실패:', err);
      setToast({ type: 'error', text: '저장에 실패했습니다.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 헤더 */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-5">
              <i className="ri-folder-open-line text-3xl text-orange-600"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">자료실</h1>
            <p className="text-gray-600 text-lg">직접 확인하고 판단하세요</p>
          </div>

          {/* 관리자 버튼 */}
          <div className="flex justify-end mb-8">
            <button
              onClick={handleAddResourceClick}
              className="flex items-center space-x-2 bg-orange-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap shadow-md"
            >
              <i className="ri-add-line text-lg"></i>
              <span>자료 등록</span>
            </button>
          </div>

          {/* 카테고리 탭 필터 */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-gray-100 rounded-full p-1.5 gap-1">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-7 py-3 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-orange-600 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* 로딩 상태 */}
          {loading && (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-500">자료를 불러오는 중...</p>
            </div>
          )}

          {/* 게시물 그리드 */}
          {!loading && filteredResources.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">등록된 자료가 없습니다</p>
            </div>
          )}

          {!loading && filteredResources.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map((resource) => (
                <div
                  key={resource.id}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  {/* 썸네일 */}
                  <div className="relative w-full h-48 bg-gray-100 overflow-hidden">
                    {resource.thumbnail ? (
                      <>
                        <img
                          src={resource.thumbnail}
                          alt={resource.title}
                          title={resource.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                        {resource.url && (
                          <div className="absolute top-3 right-3 w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <i className="ri-play-fill text-white text-xl"></i>
                          </div>
                        )}
                        {resource.file_url && !resource.url && (
                          <button
                            onClick={() =>
                              setSelectedImage({ url: resource.thumbnail!, title: resource.title })
                            }
                            className="absolute inset-0 flex items-center justify-center cursor-pointer"
                          >
                            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <i className="ri-zoom-in-line text-gray-700 text-2xl"></i>
                            </div>
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-file-text-line text-6xl text-gray-300"></i>
                      </div>
                    )}

                    {/* 관리자 수정/삭제 버튼 */}
                    {isAuthenticated && (
                      <div className="absolute top-3 left-3 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <button
                          onClick={() => handleEditResource(resource)}
                          className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer shadow-md"
                          title="수정"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteResource(resource.id)}
                          className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer shadow-md"
                          title="삭제"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 콘텐츠 */}
                  <div className="p-5">
                    {/* 카테고리 뱃지 */}
                    <div className="mb-3">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          categoryColors[resource.category as keyof typeof categoryColors] ||
                          'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {resource.category}
                      </span>
                    </div>

                    {/* 제목 */}
                    <h3 className="text-base font-semibold text-gray-900 line-clamp-2 mb-2 min-h-[3rem]">
                      {resource.title}
                    </h3>

                    {/* 설명 */}
                    {resource.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">{resource.description}</p>
                    )}

                    {/* 날짜 */}
                    {resource.published_at && (
                      <p className="text-xs text-gray-400 mb-3">{formatDate(resource.published_at)}</p>
                    )}

                    {/* 액션 버튼 */}
                    {resource.url && (
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="flex items-center text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-external-link-line mr-1.5"></i>
                        <span>자세히 보기</span>
                      </a>
                    )}

                    {resource.file_url && (
                      <button
                        onClick={() =>
                          handleDownload(resource.file_url!, resource.file_name || 'download')
                        }
                        className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-orange-50 text-orange-600 rounded-lg text-sm font-semibold hover:bg-orange-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-download-line"></i>
                        다운로드
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* 비밀번호 입력 모달 */}
      {showPasswordModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setShowPasswordModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-full mr-3">
                <i className="ri-lock-line text-orange-600 text-lg"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900">관리자 인증</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">자료실을 관리하려면 관리자 비밀번호를 입력해주세요.</p>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => {
                setAdminPassword(e.target.value);
                setPasswordError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handlePasswordSubmit();
              }}
              placeholder="관리자 비밀번호"
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm ${
                passwordError ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              autoFocus
            />
            {passwordError && (
              <p className="text-xs text-red-600 mt-1.5 flex items-center">
                <i className="ri-error-warning-line mr-1"></i>
                비밀번호가 올바르지 않습니다.
              </p>
            )}
            <div className="flex justify-end space-x-3 mt-5">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handlePasswordSubmit}
                className="px-5 py-2 bg-orange-600 text-white text-sm font-semibold rounded-lg hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 자료 등록/수정 폼 모달 */}
      {showFormModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 overflow-y-auto py-8"
          onClick={() => setShowFormModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">
                {editingResource ? '자료 수정' : '자료 등록'}
              </h3>
              <button
                onClick={() => setShowFormModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              {/* 카테고리 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  카테고리 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="언론">언론</option>
                  <option value="미디어">미디어</option>
                  <option value="캠페인">캠페인</option>
                </select>
              </div>

              {/* 제목 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="자료 제목을 입력하세요"
                  maxLength={200}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>

              {/* URL */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  링크 URL
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://example.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">외부 링크가 있는 경우 입력하세요</p>
              </div>

              {/* 썸네일 URL */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  썸네일 이미지 URL
                </label>
                <input
                  type="url"
                  value={formData.thumbnail}
                  onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>

              {/* 설명 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">설명</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, description: e.target.value });
                    }
                  }}
                  placeholder="자료에 대한 간단한 설명을 입력하세요"
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {formData.description.length}/500
                </p>
              </div>

              {/* 파일 URL */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  다운로드 파일 URL
                </label>
                <input
                  type="url"
                  value={formData.file_url}
                  onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                  placeholder="https://example.com/file.pdf"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>

              {/* 파일명 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">파일명</label>
                <input
                  type="text"
                  value={formData.file_name}
                  onChange={(e) => setFormData({ ...formData, file_name: e.target.value })}
                  placeholder="download.pdf"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>

              {/* 발행일 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">발행일</label>
                <input
                  type="date"
                  value={formData.published_at}
                  onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowFormModal(false)}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleSubmitResource}
                disabled={isSubmitting}
                className="flex items-center space-x-2 bg-orange-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors disabled:bg-gray-400 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>저장 중...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    <span>{editingResource ? '수정하기' : '등록하기'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이미지 확대 모달 */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl w-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="w-full h-auto rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute -bottom-14 left-1/2 -translate-x-1/2">
              <span className="text-white text-sm font-semibold">{selectedImage.title}</span>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-fade-in">
          <div
            className={`flex items-center space-x-2 px-5 py-3 rounded-lg shadow-xl text-sm font-medium ${
              toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}
          >
            <i
              className={
                toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'
              }
            ></i>
            <span>{toast.text}</span>
          </div>
        </div>
      )}
    </div>
  );
}